import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { usePayments } from '../hooks/usePayments';
import { receiptAPI, getErrorMessage } from '../services/api';
import CenterFilter from './CenterFilter';
import { formatDistanceToNow } from 'date-fns';
import { Eye, Download, Filter, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';

const statusConfig = {
  pending: { icon: Clock, color: 'bg-yellow-100', textColor: 'text-yellow-800', badge: 'bg-yellow-200' },
  approved: { icon: CheckCircle, color: 'bg-green-100', textColor: 'text-green-800', badge: 'bg-green-200' },
  rejected: { icon: XCircle, color: 'bg-red-100', textColor: 'text-red-800', badge: 'bg-red-200' },
  needs_info: { icon: AlertCircle, color: 'bg-blue-100', textColor: 'text-blue-800', badge: 'bg-blue-200' },
};

export default function PaymentList({ onSelectPayment, refreshToken = 0 }) {
  const { payments, loading, error, page, setPage, total, pageSize, status, setStatus, centerId, setCenterId, refresh } = usePayments();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    if (refreshToken > 0) {
      refresh();
    }
  }, [refreshToken, refresh]);

  const handleDownloadReceipt = async (payment) => {
    const receiptId = payment.receipt?.id;
    if (!receiptId) {
      toast.error('No receipt attached');
      return;
    }

    setDownloadingId(payment.id);
    try {
      const res = await receiptAPI.downloadReceipt(receiptId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', payment.receipt.original_filename || 'receipt');
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded');
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to download receipt');
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    if (payments) {
      const filtered = payments.filter(p =>
        p.transaction_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.phone_number?.includes(searchTerm) ||
        p.amount?.toString().includes(searchTerm)
      );
      setFilteredPayments(filtered);
    }
  }, [payments, searchTerm]);

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 flex items-center gap-3">
        <XCircle className="w-5 h-5" />
        <span>{error}</span>
      </div>
    );
  }

  const totalPages = Math.ceil(total / pageSize) || 1;
  const safePayments = payments ?? [];
  const displayedPayments = searchTerm ? filteredPayments : safePayments;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Payments</h1>
        <p className="text-gray-600 mt-1">Manage and review all payment submissions</p>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by reference, phone, or amount..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filter Icon */}
          <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-gray-700">
            <Filter className="w-5 h-5" />
            Filters
          </button>
        </div>

        {/* Status Filter */}
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setStatus(null)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              status === null
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All
          </button>
          {['pending', 'approved', 'rejected', 'needs_info'].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-4 py-2 rounded-lg font-medium capitalize transition-all ${
                status === s
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {s.replace('_', ' ')}
            </button>
          ))}
          </div>
          <CenterFilter
            value={centerId}
            onChange={(id) => {
              setPage(0);
              setCenterId(id);
            }}
          />
        </div>
      </div>

      {/* Payments Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : (displayedPayments ?? []).length > 0 ? (
        <div className="space-y-3">
          {displayedPayments.map((payment) => {
            const StatusIcon = statusConfig[payment.status]?.icon || Clock;
            const config = statusConfig[payment.status] || {};

            return (
              <div
                key={payment.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${config.color}`}>
                        <StatusIcon className={`w-5 h-5 ${config.textColor}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{payment.amount} Birr</h3>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${config.badge} ${config.textColor}`}>
                            {payment.status.replace('_', ' ')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{payment.payment_month}</p>
                      </div>
                    </div>

                    {/* Details Grid */}
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Reference</span>
                        <p className="font-mono text-gray-900 truncate">{payment.transaction_reference}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Center</span>
                        <p className="text-gray-900 truncate">{payment.center_name || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Phone</span>
                        <p className="font-mono text-gray-900">{payment.phone_number}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Students</span>
                        <p className="text-gray-900">
                          {payment.students?.length > 0
                            ? payment.students.map((s) => `${s.name}${s.grade ? ` (${s.grade})` : ''}`).join(', ')
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">Submitted</span>
                        <p className="text-gray-900">
                          {formatDistanceToNow(new Date(payment.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => onSelectPayment(payment.id)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View details"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                    {payment.receipt && (
                      <button
                        onClick={() => handleDownloadReceipt(payment)}
                        disabled={downloadingId === payment.id}
                        className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Download receipt"
                      >
                        <Download className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500 mb-4">No payments found</p>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear search
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && !searchTerm && (
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>

          <span className="text-sm text-gray-600">
            Page {page + 1} of {totalPages} ({total} total)
          </span>

          <button
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
